import cns from "classnames";

import Accordion from "@Components/Accordion";
import Image from "@Components/ui/Image";
import Wysiwyg from "@Components/Wysiwyg";
import { useGetPageDataFaq, useGetPageDataPage } from "@Hooks/api/page";
import { ContentPageItem, FaqResponse } from "@Types/api";

import Cards, { CardsProps as TypeCardsProps } from "./Cards";
import styles from "./index.module.scss";

export type PageProps = Record<string, never>;

type ItemProps = {
    items: ContentPageItem["items"];
    groupIndex?: number;
};

const Items: React.FC<ItemProps> = ({ items, groupIndex }) => {
    if (!items?.length) {
        return null;
    }

    return (
        <div className={styles.component__list}>
            {items.map((item, itemIndex: number) => (
                <Accordion
                    key={itemIndex}
                    className={styles.component__item}
                    title={item.title}
                    defaultOpen={!groupIndex && !itemIndex}
                >
                    {item.image ? (
                        <div className={styles.component__itemContent}>
                            <Wysiwyg
                                className={styles.component__wysiwyg}
                                content={item.content}
                            />

                            <div className={styles.component__itemImage}>
                                <Image
                                    src={item.image}
                                    alt=""
                                    width="216"
                                    height="216"
                                />
                            </div>
                        </div>
                    ) : (
                        <Wysiwyg
                            className={styles.component__wysiwyg}
                            content={item.content}
                        />
                    )}
                </Accordion>
            ))}
        </div>
    );
};

const Page: React.FC<PageProps> = () => {
    const dataPage: ContentPageItem = useGetPageDataPage();
    const dataFaq: FaqResponse = useGetPageDataFaq();

    return (
        <div className={styles.component}>
            <div
                className={cns(styles.component__container, {
                    [styles.component__container_wide]:
                        dataPage.slug === "size-guide",
                })}
            >
                <h2
                    className={cns(
                        styles.component__title,
                        "text text_h4 text_uppercase"
                    )}
                >
                    {dataPage.name}
                </h2>

                {dataPage.cards?.length && (
                    <Cards
                        className={styles.component__cards}
                        cards={dataPage.cards as TypeCardsProps["cards"]}
                    />
                )}

                {!!dataPage.texts?.length &&
                    dataPage.texts.map((text, textIndex) => (
                        <Wysiwyg
                            key={textIndex}
                            className={styles.component__wysiwyg}
                            content={text}
                        />
                    ))}

                {!!dataPage.items?.length && <Items items={dataPage.items} />}

                {!!dataFaq?.length &&
                    (dataFaq.length === 1 ? (
                        <Items items={dataFaq[0].items} />
                    ) : (
                        <div className={styles.component__groups}>
                            {dataFaq.map((group, groupIndex: number) => (
                                <div
                                    key={groupIndex}
                                    className={styles.component__group}
                                    id={`faq-${group.code || groupIndex}`}
                                >
                                    <h4
                                        className={cns(
                                            styles.component__title,
                                            "text text_h4 text_uppercase"
                                        )}
                                    >
                                        {group.title}
                                    </h4>

                                    <Items
                                        items={group.items}
                                        groupIndex={groupIndex}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default Page;
